import {afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Injector, OnInit, signal, viewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {MessagingService} from './services/messaging.service';
import {MessageConverterService} from './services/message-converter.service';
import {MessageViewModel} from './view-models/message-view.model';
import {AttachmentViewModel} from './view-models/attachment-view.model';
import {ConversationReplyRequestDTO} from '@dto/messaging/conversation-reply-request-dto.model';
import {ErrorModalComponent} from '@shared/components/error-modal/error-modal.component';
import {MessageErrorDetails} from './entities/message-error-details.model';
import {ModalService} from '../shared/services/modal.service';
import {Title} from '@angular/platform-browser';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {MessageComponent} from './message/message.component';
import {MessageInputBarComponent} from './message-input-bar/message-input-bar.component';
import {catchError, EMPTY, from, Observable, of, switchMap} from 'rxjs';
import {concatMap, toArray} from 'rxjs/operators';
import {BlobSasUploadService} from '@shared/services/blob-sas-upload.service';
import {AttachmentUploadViewModel} from './view-models/attachment-upload-view.model';
import {ConversationDetailMessageDTO} from '@dto/messaging/conversation-detail-message-dto.model';
import {MessageSendEvent} from './entities/message-send-event.model';
import {ConfirmDialog} from '@shared/components/confirm-dialog/confirm-dialog.component';

interface PendingMessageAttachmentDelete {
  message: MessageViewModel;
  attachment: AttachmentViewModel;
}

@Component({
    selector: 'app-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, FormsModule, MessageComponent, MessageInputBarComponent, ConfirmDialog]
})
export class MessagesComponent implements OnInit {

  readonly dataLoading = signal(true);
  readonly dataEmpty = signal(false);
  readonly messages = signal<MessageViewModel[]>([]);
  readonly scrollable = viewChild<ElementRef>('scrollable');
  readonly messageInputBar = viewChild(MessageInputBarComponent);
  readonly pendingDeleteAttachment = signal<PendingMessageAttachmentDelete | null>(null);

  private readonly conversationId = signal<string | null>(null);

  private readonly messagingService = inject(MessagingService);
  private readonly messageConverterService = inject(MessageConverterService);
  private readonly modalService = inject(ModalService);
  private readonly titleService = inject(Title);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly blobSasUploadService = inject(BlobSasUploadService);

  ngOnInit(): void {
    this.titleService.setTitle('Portal - Messages');
    this.loadMessages();
  }

  refreshConversation(): void {
    this.loadMessages();
  }

  private loadMessages(): void {
    this.beginLoading();

    this.messagingService.getConversations()
      .pipe(
        switchMap(response => {
          // BC returns conversations ordered oldest-first; index 0 is always the user's single conversation.
          const conversation = response[0] ?? null;
          if (conversation === null) {
            this.conversationId.set(null);
            this.messages.set([]);
            this.endLoading();
            return EMPTY;
          }
          this.conversationId.set(conversation.conversationId);
          return this.messagingService.getConversation(conversation.conversationId);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: detail => {
          this.messages.set(this.messageConverterService.convert(detail));
          this.endLoading();
        },
        error: () => {
          this.messages.set([]);
          this.endLoading();
        }
      });
  }

  messageSend(messageDetails: MessageSendEvent): void {
    const currentId = this.conversationId();

    const replyRequest: ConversationReplyRequestDTO = {
      content: messageDetails.content
    };
    const uploadAttachments = messageDetails.uploadAttachments ?? [];

    this.beginLoading();

    const sendReplyAndRefresh = () => this.messagingService.sendMessage(currentId, replyRequest)
      .pipe(
        switchMap(() => {
          if (currentId !== null) {
            return this.messagingService.getConversation(currentId);
          }
          // New conversation created — reload list to discover the new conversationId
          return this.messagingService.getConversations().pipe(
            switchMap(convos => {
              // BC returns conversations ordered oldest-first; index 0 is the newly created conversation.
              const conversation = convos[0] ?? null;
              if (conversation === null) {
                return of([]);
              }
              this.conversationId.set(conversation.conversationId);
              return this.messagingService.getConversation(conversation.conversationId);
            })
          );
        })
      );

    let sendFlow: Observable<ConversationDetailMessageDTO[]>;
    if (uploadAttachments.length === 0) {
      sendFlow = sendReplyAndRefresh();
    } else if (currentId === null) {
      // New conversation: create first, then upload blobs to the newly created message
      sendFlow = sendReplyAndRefresh().pipe(
        switchMap(detail => {
          const newConversationId = this.conversationId();
          if (newConversationId === null || detail.length === 0) {
            return of(detail);
          }
          const messageNumber = detail.at(-1)!.messageNumber;
          return this.uploadMessageAttachments(newConversationId, messageNumber, uploadAttachments).pipe(
            switchMap(() => this.messagingService.getConversation(newConversationId)),
            catchError(() => {
              this.handleErrorEvent({
                errorMessage: 'Document upload failed.',
                errorDescription: 'Something went wrong uploading your document. Please try again.'
              });
              this.endLoading();
              return EMPTY;
            })
          );
        })
      );
    } else {
      // Existing conversation: send the reply first, then upload blobs using the message number
      // assigned by BC. This eliminates the race condition where a concurrent reply between the
      // pre-fetch and the upload would cause attachment metadata to land on the wrong message slot.
      sendFlow = sendReplyAndRefresh().pipe(
        switchMap(detail => {
          if (detail.length === 0) {
            return of(detail);
          }
          const messageNumber = detail.at(-1)!.messageNumber;

          return this.uploadMessageAttachments(currentId, messageNumber, uploadAttachments).pipe(
            switchMap(() => this.messagingService.getConversation(currentId)),
            catchError(() => {
              this.handleErrorEvent({
                errorMessage: 'Document upload failed.',
                errorDescription: 'Something went wrong uploading your document. Please try again.'
              });
              this.endLoading();
              return EMPTY;
            })
          );
        })
      );
    }

    sendFlow
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.messages.set(this.messageConverterService.convert(detail));
          this.messageInputBar()?.clearMessage();
          this.endLoading();
        },
        error: () => {
          this.modalService.openModal(ErrorModalComponent, {
            errorMessage: 'Message failed to send',
            errorDescription: 'An error occurred while sending your message. Please try again.'
          } satisfies MessageErrorDetails);
          this.endLoading();
        }
      });
  }

  getAttachment(messageVM: MessageViewModel, attachmentVM: AttachmentViewModel): void {
    const currentId = this.conversationId();
    if (currentId === null) {
      return;
    }

    this.messagingService
      .requestDownloadSas(currentId, messageVM.messageNumber, attachmentVM.attachmentNumber, attachmentVM.filename)
      .pipe(
        switchMap(sas => this.blobSasUploadService.downloadViaSasUrl(sas.sasUrl, attachmentVM.filename)),
        catchError(() => {
          this.handleErrorEvent({
            errorMessage: 'Document download failed.',
            errorDescription: 'Something went wrong downloading your document. Please try again.'
          });
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  requestDeleteAttachment(messageVM: MessageViewModel, attachmentVM: AttachmentViewModel): void {
    this.pendingDeleteAttachment.set({ message: messageVM, attachment: attachmentVM });
  }

  onConfirmDeleteAttachment(): void {
    const pendingDelete = this.pendingDeleteAttachment();
    const currentId = this.conversationId();
    if (pendingDelete === null || currentId === null) {
      this.pendingDeleteAttachment.set(null);
      return;
    }

    this.pendingDeleteAttachment.set(null);
    this.beginLoading();

    this.messagingService
      .requestDeleteSas(
        currentId,
        pendingDelete.message.messageNumber,
        pendingDelete.attachment.attachmentNumber,
        pendingDelete.attachment.filename)
      .pipe(
        switchMap(() => this.messagingService.getConversation(currentId)),
        catchError(() => {
          this.handleErrorEvent({
            errorMessage: 'Document delete failed.',
            errorDescription: 'Something went wrong deleting your document. Please try again.'
          });
          this.endLoading();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: detail => {
          this.messages.set(this.messageConverterService.convert(detail));
          this.endLoading();
        }
      });
  }

  onCancelDeleteAttachment(): void {
    this.pendingDeleteAttachment.set(null);
  }

  handleErrorEvent(errorDetails: MessageErrorDetails): void {
    this.modalService.openModal(ErrorModalComponent, errorDetails);
  }

  private beginLoading(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.scrollToBottomAfterRender();
  }

  private endLoading(): void {
    this.dataLoading.set(false);
    this.dataEmpty.set(this.messages().length === 0);
    this.scrollToBottomAfterRender();
  }

  private uploadMessageAttachments(conversationId: string, messageNumber: number, uploadAttachments: AttachmentUploadViewModel[]) {
    return from(uploadAttachments).pipe(
      concatMap((attachment, index) => {
        if (!attachment.file) {
          throw new Error('File payload missing');
        }

        const file = attachment.file;
        const attachmentNumber = index + 1;
        return this.messagingService
          .requestUploadSas(conversationId, messageNumber, attachmentNumber, attachment.filename)
          .pipe(switchMap(sas => this.blobSasUploadService.uploadToSasUrl(sas.sasUrl, file)));
      }),
      toArray()
    );
  }

  private scrollToBottomAfterRender(): void {
    afterNextRender(() => this.scrollToBottom(), { injector: this.injector });
  }

  private scrollToBottom(): void {
    try {
      this.scrollable()?.nativeElement.scrollTo(0, this.scrollable()?.nativeElement.scrollHeight);
    } catch {
      // Scroll failure is non-critical; element may not be rendered yet
    }
  }
}
